/** Which shape of the layout a reader was actually looking at. */
export type ReportingDevice = 'Mobile' | 'Desktop';

/**
 * The `md` breakpoint, which is where every layout in the app forks.
 *
 * Tailwind's default, and the configs override only `2xl` and `qhd`, so this is
 * the same 768px the `md:` variants compile against.
 */
const DESKTOP_MIN_WIDTH_PX = 768;

/**
 * Which layout the reader is being shown, for reporting.
 *
 * Viewport rather than user agent, deliberately. The question a funnel is
 * asking is "did this reader see the small layout or the wide one", and the
 * `md:` variants answer that from width alone — a phone held sideways gets the
 * wide layout and should be counted with it, because the wide layout is what it
 * was given. A user-agent check would answer a different question and disagree
 * with the screenshots.
 *
 * Read at the moment of the event rather than held in state: a reader who
 * rotates mid-flow genuinely changes which layout the next step is answered on,
 * and the funnel should say so.
 *
 * Defaults to `Desktop` where there is no window to measure — server render,
 * or a test environment without `matchMedia`. Only reachable from a code path
 * that has already checked for a window, so this is a floor, not a real answer.
 */
export const reportingDevice = (): ReportingDevice => {
    if (typeof window === 'undefined' || !window.matchMedia) return 'Desktop';

    return window.matchMedia(`(min-width: ${DESKTOP_MIN_WIDTH_PX}px)`).matches
        ? 'Desktop'
        : 'Mobile';
};
